import { LoadingState } from '../ui';
// src/pages/KnockoutLeaderboard.jsx

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { Button as FcButton, Card as FcCard, Table as FcTable } from '../ui';

export default function KnockoutLeaderboard({ session, userRole, username }) {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [winnerId, setWinnerId] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [friendIds, setFriendIds] = useState(new Set());

  // Logout handler for navbars
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  useEffect(() => {
    const fetchKnockoutLeaderboard = async () => {
      try {
        // Fetch user's friends
        if (session?.user?.id) {
          const { data: friendshipsData } = await supabase
            .from('friendships')
            .select('user1_id, user2_id')
            .or(`user1_id.eq.${session.user.id},user2_id.eq.${session.user.id}`);

          const friendIdSet = new Set();
          friendshipsData?.forEach(friendship => {
            if (friendship.user1_id === session.user.id) {
              friendIdSet.add(friendship.user2_id);
            } else {
              friendIdSet.add(friendship.user1_id);
            }
          });
          setFriendIds(friendIdSet);
        }

        // Fetch competition details (for name)
        const { data: compData } = await supabase
          .from("competitions")
          .select("id, name, knockout_winner")
          .eq("id", competitionId)
          .single();
        setCompetition(compData);
        if (compData?.knockout_winner) {
          setWinnerId(compData.knockout_winner);
        }

        const { data } = await supabase
          .from("knockout_leaderboard_view")
          .select("*")
          .eq("competition_id", competitionId);

        // Exclude entries awaiting approval / rejected from the public leaderboard.
        const approved = (data || []).filter((r) => !['pending', 'rejected'].includes(r.status));

        const sorted = approved.sort((a, b) => {
          const isAActive = a.status === "Still In" || a.status === "active";
          const isBActive = b.status === "Still In" || b.status === "active";
          if (isAActive && !isBActive) return -1;
          if (!isAActive && isBActive) return 1;
          if (isAActive && isBActive) {
            return a.user_username.localeCompare(b.user_username);
          }
          return (b.knocked_out_on || 0) - (a.knocked_out_on || 0);
        });
        setLeaderboard(sorted);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchKnockoutLeaderboard();
  }, [competitionId]);

  // Responsive green banner for mobile
  const MobileBanner = () => (
    <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-40">
      <span className="text-white text-lg font-semibold">Footy Coach</span>
    </div>
  );

  // Helper: render status label
  const statusLabel = (status) => {
    if (status === "Still In" || status === "active") return "Active";
    return "Eliminated";
  };

  // Helper: render username with friend badge
  const renderUsername = (userId, username) => {
    const isFriend = friendIds.has(userId);
    return (
      <span className="flex items-center gap-1">
        {username}
        {isFriend && <span className="text-lg" title="Friend">🤝</span>}
      </span>
    );
  };

  // Multi-entry: distinguish a user's extra entries (entry 1 shows the plain name).
  const nameFor = (entry) =>
    entry.entry_label || (entry.entry_index > 1 ? `${entry.user_username} (${entry.entry_index})` : entry.user_username);

  // Helper: render elimination matchday
  const eliminationMatchday = (entry) => {
    if (entry.status === "Still In" || entry.status === "active") return "--";
    return entry.knocked_out_on ? `Match Day ${entry.knocked_out_on}` : "--";
  };

  // Helper: determine if selection window is closed (locked)
  // For knockout, assume selection is locked if current_match_day is not today or if a lock flag is present
  // Here, we check if the competition object has a selection_locked or similar, else fallback to match_day logic
  const isSelectionLocked = () => {
    if (!competition || !competition.current_match_day || !competition.first_kickoff_time) return false;

    const now = new Date();
    const kickoffTime = new Date(competition.first_kickoff_time);

    // Lock selections 1 hour before kickoff
    const lockTime = new Date(kickoffTime.getTime() - 60 * 60 * 1000);

    return now > lockTime;
  };

  // Helper: show current selection only if player is active or selection window is closed
  const showCurrentSelection = (entry) => {
    if (entry.status !== "Still In" && entry.status !== "active") {
      return <em className="text-content-secondary italic">Eliminated</em>;
    }
    if (entry.user_username === username) {
      return entry.current_selection || "--";
    }
    if (!isSelectionLocked()) {
      return <em className="text-content-secondary italic" title="Selections will be revealed when no further selections can be made.">Hidden</em>;
    }
    return entry.current_selection || "--";
  };

  // Find current user in leaderboard
  const currentUserEntry = leaderboard.find((u) => u.user_username === username);

  // Pagination state
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(leaderboard.length / pageSize));
  const paginatedLeaderboard = leaderboard.slice((page - 1) * pageSize, page * pageSize);

  // Reset page if leaderboard changes
  useEffect(() => { setPage(1); }, [leaderboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <MobileBanner />
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
          <LoadingState label="Loading leaderboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      <MobileBanner />
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
        <h2 className="text-2xl font-bold mb-2 text-brand-text">
          Knockout Leaderboard{competition?.name ? ` - ${competition.name}` : ""}
        </h2>

        {/* Your Standings (current user) */}
        <div className="mb-8">
          <div className="font-bold text-lg text-brand-text mb-2">Your Standings</div>
          <FcCard as="div" padding="none" className="bg-surface-card rounded-lg shadow border border-ui-border overflow-x-auto">
            {/* Responsive: show only Status and Player Name on mobile */}
            <FcTable label="Knockout Leaderboard table 1" className="w-full text-left border-separate md:table hidden" style={{ borderSpacing: 0 }}>
              <thead className="bg-ui-green text-white">
                <tr>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/4">Status</th>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/4">Player Name</th>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/4">Current Selection</th>
                  <th scope="col" className="p-2 font-semibold w-1/4">Elimination Matchday</th>
                </tr>
              </thead>
              <tbody>
                {currentUserEntry ? (
                  <tr>
                    <td className="p-2">{statusLabel(currentUserEntry.status)}</td>
                    <td className="p-2">{renderUsername(currentUserEntry.user_id, currentUserEntry.user_username)}</td>
                    <td className="p-2">{showCurrentSelection(currentUserEntry) ? (currentUserEntry.current_selection || "--") : "--"}</td>
                    <td className="p-2">{eliminationMatchday(currentUserEntry)}</td>
                  </tr>
                ) : (
                  <tr>
                    <td className="p-2" colSpan={4}>Not found</td>
                  </tr>
                )}
              </tbody>
            </FcTable>
            {/* Mobile: only Status and Player Name */}
            <FcTable label="Knockout Leaderboard table 2" className="w-full text-left border-separate md:hidden" style={{ borderSpacing: 0 }}>
              <thead className="bg-ui-green text-white">
                <tr>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/2">Status</th>
                  <th scope="col" className="p-2 font-semibold w-1/2">Player Name</th>
                </tr>
              </thead>
              <tbody>
                {currentUserEntry ? (
                  <tr>
                    <td className="p-2">{statusLabel(currentUserEntry.status)}</td>
                    <td className="p-2">{renderUsername(currentUserEntry.user_id, currentUserEntry.user_username)}</td>
                  </tr>
                ) : (
                  <tr>
                    <td className="p-2" colSpan={2}>Not found</td>
                  </tr>
                )}
              </tbody>
            </FcTable>
          </FcCard>
        </div>

        {/* Competition Standings */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="font-bold text-lg text-brand-text">Competition Standings</div>
            <a
              href={`/competition/${competitionId}`}
              className="font-semibold text-sm hover:underline focus:underline focus:outline-none px-2 py-1 rounded transition-colors"
              style={{ color: '#BA6830' }}
            >
              View Competition
            </a>
          </div>
          <FcCard as="div" padding="none" className="bg-surface-card rounded-lg shadow border border-ui-border overflow-x-auto">
            {/* Desktop/full: all columns */}
            <FcTable label="Knockout Leaderboard table 3" className="w-full text-left border-separate md:table hidden" style={{ borderSpacing: 0 }}>
              <thead className="bg-ui-green text-white">
                <tr>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/4">Status</th>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/4">Player Name</th>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/4">Current Selection</th>
                  <th scope="col" className="p-2 font-semibold w-1/4">Elimination Matchday</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeaderboard.map((entry) => (
                  <tr key={entry.entry_id || entry.user_id}>
                    <td className="p-2">{statusLabel(entry.status)}</td>
                    <td className="p-2">{renderUsername(entry.user_id, nameFor(entry))}</td>
                    <td className="p-2">{showCurrentSelection(entry)}</td>
                    <td className="p-2">{eliminationMatchday(entry)}</td>
                  </tr>
                ))}
              </tbody>
            </FcTable>
            {/* Mobile: only Status and Player Name */}
            <FcTable label="Knockout Leaderboard table 4" className="w-full text-left border-separate md:hidden" style={{ borderSpacing: 0 }}>
              <thead className="bg-ui-green text-white">
                <tr>
                  <th scope="col" className="p-2 font-semibold border-r border-white w-1/2">Status</th>
                  <th scope="col" className="p-2 font-semibold w-1/2">Player Name</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeaderboard.map((entry) => (
                  <tr key={entry.entry_id || entry.user_id}>
                    <td className="p-2">{statusLabel(entry.status)}</td>
                    <td className="p-2">{renderUsername(entry.user_id, nameFor(entry))}</td>
                  </tr>
                ))}
              </tbody>
            </FcTable>
            {/* Pagination */}
            <div className="flex items-center justify-center gap-4 mt-4 px-4 pb-2">
              <FcButton variant="tertiary" type="submit"
                className={`${page === 1 ? "cursor-not-allowed" : ""}`}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </FcButton>
              <span className="text-brand-text font-medium">
                Page {page} of {totalPages}
              </span>
              <FcButton variant="tertiary" type="submit"
                className={`${page === totalPages ? "cursor-not-allowed" : ""}`}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </FcButton>
            </div>
          </FcCard>
        </div>
      </div>
    </div>
  );
}
